import { Link, useLocation, useNavigate } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../data/content";
import { scrollToHash } from "./Navbar";

const LEGAL_LINKS = [
    { label: "Privacy Policy", to: "/privacy-policy", testId: "legal-link-privacy" },
    { label: "Terms & Conditions", to: "/terms-and-conditions", testId: "legal-link-terms" },
    { label: "Cookie Policy", to: "/cookie-policy", testId: "legal-link-cookies" },
];

export const Footer = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const go = (hash) => {
        if (location.pathname !== "/") {
            navigate("/" + hash);
        } else {
            window.history.replaceState(null, "", hash);
            scrollToHash(hash);
        }
    };

    return (
        <footer data-testid="footer" className="bg-navy-950 text-slate-400">
            <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center bg-white/10 font-heading text-lg font-bold text-gold">
                                P
                            </span>
                            <span className="leading-tight">
                                <span className="block font-heading text-[15px] font-bold tracking-tight text-white">
                                    {COMPANY.name}
                                </span>
                                <span className="block text-[10px] font-medium uppercase tracking-[0.22em] text-slate-500">
                                    Wholesale &amp; Retail Products
                                </span>
                            </span>
                        </div>
                        <p className="mt-5 text-sm leading-relaxed text-slate-400">
                            A UK-based wholesale and retail business supplying everyday consumer goods,
                            groceries, beverages and household products to customers and businesses.
                        </p>
                    </div>

                    <nav aria-label="Footer navigation">
                        <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-white">
                            Navigation
                        </h3>
                        <ul className="mt-5 space-y-3">
                            {NAV_LINKS.map((link) => (
                                <li key={link.hash}>
                                    <button
                                        type="button"
                                        data-testid={`footer-${link.testId}`}
                                        onClick={() => go(link.hash)}
                                        className="text-sm text-slate-400 transition-colors hover:text-white"
                                    >
                                        {link.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-white">
                            Legal
                        </h3>
                        <ul className="mt-5 space-y-3">
                            {LEGAL_LINKS.map((link) => (
                                <li key={link.to}>
                                    <Link
                                        to={link.to}
                                        data-testid={link.testId}
                                        className="text-sm text-slate-400 transition-colors hover:text-white"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-white">
                            Contact
                        </h3>
                        <ul className="mt-5 space-y-4 text-sm">
                            <li className="flex items-start gap-3">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                                <span>{COMPANY.addressInline}</span>
                            </li>
                            <li>
                                <a
                                    href={COMPANY.phoneHref}
                                    data-testid="footer-phone-link"
                                    className="flex items-center gap-3 transition-colors hover:text-white"
                                >
                                    <Phone className="h-4 w-4 shrink-0 text-gold" />
                                    {COMPANY.phone}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={COMPANY.emailHref}
                                    data-testid="footer-email-link"
                                    className="flex items-center gap-3 break-all transition-colors hover:text-white"
                                >
                                    <Mail className="h-4 w-4 shrink-0 text-gold" />
                                    {COMPANY.email}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
                    <p className="text-xs text-slate-500">
                        &copy; 2026 PISI LIMITED. All rights reserved.
                    </p>
                    <div className="flex gap-6">
                        {LEGAL_LINKS.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className="text-xs text-slate-500 transition-colors hover:text-white"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};
